import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Code2, 
  Database, 
  Lightbulb, 
  Users, 
  Globe, 
  Clock, 
  Shield,
  TrendingUp,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';

const Home = () => {
  const [stats, setStats] = useState({
    experts: 0,
    clients: 0,
    projects: 0,
    retention: 0
  });

  // Animate stats on mount
  useEffect(() => {
    const targetStats = { experts: 50, clients: 100, projects: 250, retention: 98 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      setStats({
        experts: Math.floor(targetStats.experts * easeOut),
        clients: Math.floor(targetStats.clients * easeOut),
        projects: Math.floor(targetStats.projects * easeOut),
        retention: Math.floor(targetStats.retention * easeOut),
      });

      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const services = [
    {
      icon: <Code2 className="w-8 h-8" />,
      title: 'IT Development',
      description: 'Web applications, custom software, and legacy modernization with enterprise-grade quality.',
      link: '/services/it-development'
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: 'ERP Solutions',
      description: 'Oracle, SAP, Salesforce, and Microsoft Dynamics implementation and support.',
      link: '/services/erp-solutions'
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'IT Consulting',
      description: 'Digital transformation, cloud strategy, and enterprise architecture guidance.',
      link: '/services/it-consulting'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Manpower Consulting',
      description: 'Staff augmentation and dedicated teams of pre-vetted technology experts.',
      link: '/services/manpower-consulting'
    }
  ];

  const valueProps = [
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Global Delivery',
      description: 'Seamless collaboration across Dubai and India delivery centers'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: '24/7 Support',
      description: 'Round-the-clock expert assistance for your critical systems'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Enterprise Security',
      description: 'Compliance-ready solutions with industry-standard security'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Proven ROI',
      description: 'Average 35% cost reduction for our ERP optimization clients'
    }
  ];

  const industries = [
    'Financial Services',
    'Healthcare',
    'Manufacturing',
    'Retail',
    'Real Estate',
    'Logistics'
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center gradient-hero">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm">
                <span className="w-2 h-2 bg-teal rounded-full animate-pulse"></span>
                Trusted by 100+ Enterprise Clients
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Transforming Enterprises Through{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-teal">
                Technology Excellence
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl"
            >
              Premium IT consulting, ERP solutions, and dedicated teams for enterprises 
              in Dubai and India. Global delivery capability with local market expertise.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-teal btn-lg">
                Schedule a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/services" className="btn bg-white/10 text-white hover:bg-white/20 border border-white/20 btn-lg">
                Explore Our Services
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div variants={fadeInUp} className="mt-16 pt-8 border-t border-white/10">
              <p className="text-white/60 text-sm mb-4">Certified Partners</p>
              <div className="flex flex-wrap gap-6 items-center">
                {['Oracle', 'SAP', 'Salesforce', 'Microsoft', 'AWS'].map((partner) => (
                  <span key={partner} className="text-white/40 font-semibold text-lg">
                    {partner}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: stats.experts, suffix: '+', label: 'Technology Experts' },
              { value: stats.clients, suffix: '+', label: 'Enterprise Clients' },
              { value: stats.projects, suffix: '+', label: 'Projects Delivered' },
              { value: stats.retention, suffix: '%', label: 'Client Retention' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.value}{stat.suffix}
                </div>
                <p className="text-slate-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Comprehensive Technology Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              End-to-end IT services designed to accelerate your digital transformation 
              and maximize technology ROI.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="card-hover p-8"
              >
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center text-accent mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center text-accent font-semibold hover:text-accent-dark transition-colors"
                >
                  Learn More
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-accent font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-6">
                Your Trusted Technology Partner
              </h2>
              <p className="text-slate-600 mb-8">
                We combine technical excellence with strategic insight to help enterprises 
                navigate digital transformation with confidence. Our global delivery model 
                ensures cost-effective solutions without compromising quality.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {valueProps.map((prop, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center text-teal flex-shrink-0">
                      {prop.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">{prop.title}</h4>
                      <p className="text-sm text-slate-600">{prop.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-white font-semibold text-lg">
                    "TeqDeepSeek transformed our operations with their ERP expertise."
                  </p>
                  <p className="text-white/70 text-sm mt-2">
                    — CTO, Leading UAE Financial Institution
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section gradient-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">Industries</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4">
              Industries We Transform
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Deep domain expertise combined with technical excellence to deliver 
              solutions that address your industry's unique challenges.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex items-center gap-4 p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors cursor-pointer"
              >
                <CheckCircle2 className="w-6 h-6 text-teal flex-shrink-0" />
                <span className="font-semibold">{industry}</span>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-10">
            <Link to="/industries" className="btn bg-white/10 text-white hover:bg-white/20 border border-white/20">
              View All Industries
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Enterprise?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Let's discuss how our expertise can accelerate your digital transformation 
              journey and deliver measurable business results.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn bg-white text-accent hover:bg-slate-100 btn-lg">
                Schedule a Free Consultation
              </Link>
              <Link to="/services" className="btn bg-transparent text-white hover:bg-white/10 border border-white/30 btn-lg">
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
